import { Injectable } from '@angular/core';
import { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import { CognitoIdToken } from 'amazon-cognito-identity-js';
import { AwsconfigService } from '@features/authentication/services/awsconfig.service';
import { CanActivate, CanLoad, Router } from '@angular/router';
import { INIT_STATUS } from './namespace/namespace-init.service';
import { ActiveNamespaceService } from './namespace/active-namespace.service';
import { publicRoutes } from '@pages/routesPublic';
import { CurrentRouteService } from './currentRoute.service';

const REPEAT_INTERVAL = 500;
@Injectable({
  providedIn: 'root'
})
export class TokenService implements CanActivate, CanLoad {

  constructor(
    private awsService: AwsconfigService,
    private activeNamespaceService: ActiveNamespaceService,
    private router: Router,
    private currentRouteService: CurrentRouteService,
  ) {}
  getToken(): string | null {
    return localStorage.getItem('token');
  }

  setToken(token: string): void {
    localStorage.setItem('token', token);
  }

  hasToken(): boolean {
    return !!this.getToken();
  }

  async isTokenExpired(): Promise<boolean> {
    const token = this.getToken();
    const today = new Date().getTime() / 1000;
    this.applyAwsConfig();

    if (!token) {
      return true;
    }

    try {
      const currentSession = await Auth.currentSession();
      const tokenExpirySession = currentSession.getIdToken().getExpiration();
      const tokenJwtToken = currentSession.getIdToken().getJwtToken();
  
      const cognitoIdToken = new CognitoIdToken({
        IdToken: token,
      });
      const tokenExpiryStore = cognitoIdToken.getExpiration();
      if (tokenExpirySession != tokenExpiryStore) {
        this.setToken(tokenJwtToken);
      }
  
      return today > tokenExpirySession;
    }
    catch (error) {
      const isPublicRoute = this.currentRouteService.isCurrentRouteExistIn(publicRoutes);
      if (!isPublicRoute) {
        this.router.navigate(['/logout']);
      }
      return true;
    }
  }

  async refreshToken(forceRefresh = false): Promise<string | null> {
    const isTokenExpired = await this.isTokenExpired();
    if (!isTokenExpired && !forceRefresh) {
      return this.getToken();
    }
    const cognitoUser: CognitoUser = await Auth.currentAuthenticatedUser();
    let currentSession = await Auth.currentSession();
    const refreshToken = currentSession.getRefreshToken();

    cognitoUser.refreshSession(
      refreshToken,
      (session: CognitoUserSession) => {
        const currentIdToken = session.getIdToken();
        const currentToken = currentIdToken.getJwtToken();
        this.setToken(currentToken);
        return instanceId
      }
    );
    currentSession = await Auth.currentSession();
    const instanceId = currentSession.getIdToken().payload['custom:instances'];
    const token = currentSession.getIdToken().getJwtToken();
    if (forceRefresh) {
      return instanceId ?? null
    }
    return token
  }

  canActivate(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const id = setInterval(
        () => {
          const status = localStorage.getItem('initializationStatus');
          if (status === INIT_STATUS.RESOLVED) {
            clearInterval(id);
            Auth.currentAuthenticatedUser()
              .then(() => {
                this.awsService.applyConfigLoginPassword();
                return resolve(true)
              })
              .catch(() => reject(false));
          } else if (status === INIT_STATUS.REJECTED) {
            clearInterval(id);
            localStorage.clear();
            reject(false);
          }
        },
        REPEAT_INTERVAL,
      );
    }).catch((error) => {
      console.error(error);
      this.router.navigate(['/logout']);
      return false;
    }) as Promise<boolean>;
  }

  canLoad(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const id = setInterval(
        () => {
          const status = localStorage.getItem('initializationStatus');
          if (status === INIT_STATUS.RESOLVED) {
            clearInterval(id);
            Auth.currentAuthenticatedUser()
              .then(() => {
                this.awsService.applyConfigLoginPassword();
                return resolve(true)
              })
              .catch(() => reject(false));
          } else if (status === INIT_STATUS.REJECTED) {
            clearInterval(id);
            localStorage.clear();
            reject(false);
          }
        },
        REPEAT_INTERVAL,
      );
    }).catch((error) => {
      console.error(error);
      this.router.navigate(['/logout']);
      return false;
    }) as Promise<boolean>;
  }

  applyAwsConfig(): void {
    const isGoogleUser = localStorage.getItem('clientid') === 'google';
    const namespace = this.activeNamespaceService.getNamespace();
    if (!namespace) {
      return;
    }
    this.awsService.reconfigureAuth(namespace.userPoolId, !isGoogleUser ? namespace.oauthClientId : namespace.oauthClientIdGoogle);
  }

}
